/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { 
  Box, 
  Flex, 
  Button, 
  Text,
  Card
} from '@infotrack/zenith-ui';
import { Progress } from '@/shared/components/progress/progress';
import { BonusUploadForm } from '../components/bonusUploadForm';
import { pageStyles, contentStyles, headerTextStyles, titleStyles, subtitleStyles, cardStyles, resultsSectionStyles } from './bonusesPage.styles';
import { useCalculateBonuses, type StaffTopFiveBonusItem } from '../hooks';

/**
 * Calculate Bonuses page
 * Allows users to upload a CSV file with staff bonus amounts and calculate
 * bonus distribution based on TopFive item weights and results.
 * Similar to ImportPage for consistent UX.
 */
export const BonusesPage: React.FC = () => {
  const [results, setResults] = useState<StaffTopFiveBonusItem[] | null>(null);
  const [pageState, setPageState] = useState<'upload' | 'processing' | 'results'>('upload');
  const { calculateBonuses, downloadResultsAsCsv, isCalculating, progress, error, resetState } = useCalculateBonuses();

  const handleDownloadTemplate = () => {
    const template = 'FirstName,Surname,EmployeeId,Amount\n,,,';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'BonusTemplate.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (data: { file: File; startDate: string; endDate: string }) => {
    setPageState('processing');

    const response = await calculateBonuses(data.file, data.startDate, data.endDate);
    
    if (response?.staffTopFiveBonuses) {
      setResults(response.staffTopFiveBonuses);
      setPageState('results');
      
      // Auto-download CSV
      downloadResultsAsCsv(response.staffTopFiveBonuses);
    } else {
      // Error occurred - go back to upload
      setPageState('upload');
    }
  };

  const handleDownloadResults = () => {
    if (results) {
      downloadResultsAsCsv(results);
    }
  };

  const handleStartNew = () => {
    setResults(null);
    setPageState('upload');
    resetState();
  };

  return (
    <Flex sx={pageStyles} as="main">
      <Box sx={contentStyles} as="section" aria-label="Calculate Bonuses">
        {/* Page Header */}
        <Box sx={headerTextStyles}>
          <Box sx={titleStyles}>
            <Text variant="title" as="h1">
              Calculate TopFive Bonuses
            </Text>
          </Box>
          <Box sx={subtitleStyles}>
            <Text variant="medium">
              Calculate bonus distribution for staff members based on TopFive item weights and results
            </Text>
          </Box>
          <Box sx={{ marginTop: '0.5rem' }}>
            <Text variant="small">
              Need help getting started?{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleDownloadTemplate();
                }}
                style={{
                  color: '#0066cc',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Download CSV template
              </a>
            </Text>
          </Box>
        </Box>

        {/* Main Content Card */}
        <Card
          padding="large"
          borderRadius="medium"
          borderColor="neutral/200"
          boxShadow="medium"
          sx={cardStyles}
        >
          {/* Upload View */}
          {pageState === 'upload' && (
            <BonusUploadForm onSubmit={handleSubmit} isCalculating={isCalculating} />
          )}

          {/* Processing View */}
          {pageState === 'processing' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ marginBottom: 2 }}>
                  <Text variant="title" as="h3">Processing Bonus Calculation</Text>
                </Box>
                <Box sx={{ color: 'neutral/600' }}>
                  <Text variant="medium">Please wait while we calculate bonuses...</Text>
                </Box>
              </Box>

              {progress.percentage > 0 && (
                <Box sx={{ width: '100%', maxWidth: '400px' }}>
                  <Progress color="#0066cc" progressValue={progress.percentage} />
                  <Box sx={{ marginTop: 2, textAlign: 'center' }}>
                    <Text variant="title">{progress.percentage}%</Text>
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  padding: 3,
                  backgroundColor: 'info/10',
                  borderRadius: 'medium',
                  borderLeft: '4px solid',
                  borderLeftColor: 'info',
                  width: '100%',
                }}
              >
                <Box sx={{ color: 'neutral/700' }}>
                  <Text variant="small">
                    The calculation process validates staff data, retrieves TopFive goals, and calculates
                    bonus distribution based on weights and results. This may take a few moments.
                  </Text>
                </Box>
              </Box>
            </Box>
          )}

          {/* Error Display */}
          {error && pageState === 'upload' && (
            <Box sx={{ 
              mb: 3,
              padding: 3,
              backgroundColor: '#fee',
              borderRadius: 'medium',
              borderLeft: '4px solid',
              borderLeftColor: '#d00',
              color: '#c00'
            }}>
              <Text variant="medium">{error}</Text>
            </Box>
          )}
        </Card>

        {/* Results Section - With Data */}
        {pageState === 'results' && results && results.length > 0 && (
          <Card padding="large" borderRadius="medium" borderColor="neutral/200" boxShadow="medium" sx={resultsSectionStyles}>
            <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Text variant="title" as="h2">
                Calculation Results
              </Text>
              <Flex sx={{ gap: 2 }}>
                <Button
                  variant="secondary"
                  onClick={handleDownloadResults}
                  size="small"
                >
                  Download CSV
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleStartNew}
                  size="small"
                >
                  Start New
                </Button>
              </Flex>
            </Flex>

            <Box sx={{ 
              mb: 3,
              padding: 3,
              backgroundColor: '#efe',
              borderRadius: 'medium',
              borderLeft: '4px solid',
              borderLeftColor: '#0a0',
              color: '#060'
            }}>
              <Text variant="medium">
                Successfully calculated bonuses for {new Set(results.map(r => r.employeeId)).size} staff members.
                Results have been automatically downloaded.
              </Text>
            </Box>

            <Box sx={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'border',
              borderRadius: 1,
              p: 2
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Name</th>
                    <th style={{ padding: '8px' }}>Employee ID</th>
                    <th style={{ padding: '8px' }}>Department</th>
                    <th style={{ padding: '8px' }}>Goal</th>
                    <th style={{ padding: '8px' }}>Weight</th>
                    <th style={{ padding: '8px' }}>Result</th>
                    <th style={{ padding: '8px' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {results.slice(0, 50).map((item, index) => (
                    <tr 
                      key={index}
                      style={{ 
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: index % 2 === 0 ? 'transparent' : '#fafafa'
                      }}
                    >
                      <td style={{ padding: '8px' }}>
                        {item.firstName} {item.surname}
                      </td>
                      <td style={{ padding: '8px' }}>{item.employeeId}</td>
                      <td style={{ padding: '8px' }}>{item.department}</td>
                      <td style={{ padding: '8px' }}>{item.name}</td>
                      <td style={{ padding: '8px' }}>{item.weight}%</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ 
                          color: item.result === 'Pass' ? 'green' : 
                                 item.result === 'Fail' ? 'red' : 'gray'
                        }}>
                          {item.result}
                        </span>
                      </td>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>
                        ${item.amount?.toFixed(2) || '0.00'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {results.length > 50 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Text variant="small" color="neutral/600">
                    Showing first 50 of {results.length} results. Download CSV to see all.
                  </Text>
                </Box>
              )}
            </Box>
          </Card>
        )}

        {/* Results Section - No Data */}
        {pageState === 'results' && results && results.length === 0 && (
          <Card padding="large" borderRadius="medium" borderColor="neutral/200" boxShadow="medium" sx={resultsSectionStyles}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{ mb: 3 }}>
                <Text variant="title" as="h2">
                  No TopFive Data Found
                </Text>
              </Box>
              
              <Box 
                sx={{ 
                  mb: 4,
                  padding: 3,
                  backgroundColor: '#fff4e5',
                  borderRadius: 'medium',
                  borderLeft: '4px solid',
                  borderLeftColor: '#ff9800',
                  color: '#663c00',
                  maxWidth: '600px',
                  margin: '0 auto 2rem auto'
                }}
              >
                <Text variant="medium">
                  No TopFive data was found for the selected period. This could mean:
                </Text>
                <ul style={{ textAlign: 'left', marginTop: '1rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                  <li>No staff members in the file have TopFive goals set up for this period</li>
                  <li>The selected date range doesn't match any active TopFive periods</li>
                  <li>The employee IDs in your CSV don't match any staff in the system</li>
                </ul>
              </Box>

              <Flex sx={{ gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="primary"
                  onClick={handleStartNew}
                  size="small"
                >
                  Try Again
                </Button>
              </Flex>
            </Box>
          </Card>
        )}
      </Box>
    </Flex>
  );
};
